import './AddNewEntity.page.css';
import { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../ui/components/LoadingSpinner';
import SidePanelScaffold from '../../ui/pageScaffolds/SidePanelScaffold';
import AdminSideBarContent from '../../ui/panels/SideBars/AdminSideBarContent';
import MainLogo from '../../ui/components/MainLogo';
import InputField from '../../ui/components/InputField';
import PageBlocker from '../../ui/components/PageBlocker';
import { LoginState } from '../../types/LoginState';
import BugButton from '../../ui/components/BugButton/BugButton';
import FeedBackButton from '../../ui/components/FeedBackButton/FeedBackButton';

interface EntityFormData {
  entityName: string;
  entityNameError: string;
  entityEmail: string;
  entityEmailError: string;
  formError: string;
}

function AddNewEntityPage(): JSX.Element {
  const token = localStorage.getItem('token');
  const nav = useNavigate();

  const [entityFormData, setEntityFormData] = useState<EntityFormData>({
    entityName: '',
    entityNameError: '',
    entityEmail: '',
    entityEmailError: '',
    formError: '',
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });

  const validateUserLogin = async () => {
    if (token) {
      const url = '/validate';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        localStorage.removeItem('token');
        nav('/');
        return;
      }
      const json = await response.json();

      if (json.valid && json.valid === true) {
        setLoginState({ isLoggedIn: true, isAdmin: true });
      } else {
        localStorage.removeItem('token');
        nav('/');
      }
    }
  };

  useEffect(() => {
    validateUserLogin();
  }, []);

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEntityNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEntityFormData({
      ...entityFormData,
      entityName: event.target.value,
      entityNameError:
        event.target.value.length < 3 && event.target.value !== ''
          ? 'Entity name is too short (minimum 3 characters)'
          : '',
      formError: '',
    });
  };

  const handleEntityEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEntityFormData({
      ...entityFormData,
      entityEmail: event.target.value,
      entityEmailError:
        !validateEmail(event.target.value) && event.target.value !== ''
          ? 'Invalid email format'
          : '',
      formError: '',
    });
  };

  const handleFormClear = () => {
    setEntityFormData({
      entityName: '',
      entityNameError: '',
      entityEmail: '',
      entityEmailError: '',
      formError: '',
    });
  };

  const handleEntityCreation = async () => {
    if (entityFormData.entityNameError !== '') return;
    if (entityFormData.entityEmailError !== '') return;

    if (entityFormData.entityName === '') {
      setEntityFormData({
        ...entityFormData,
        entityNameError: 'Entity name is required',
      });
      return;
    }

    if (entityFormData.entityEmail === '') {
      setEntityFormData({
        ...entityFormData,
        entityEmailError: 'Entity email is required',
      });
      return;
    }

    const jsonData = JSON.stringify({
      entityName: entityFormData.entityName,
      entityEmail: entityFormData.entityEmail,
    });

    const url = '/admin/entity/add';
    setLoading(true);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: jsonData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          nav('/signin');
        } else if (response.status === 400) {
          throw new Error('Invalid request. Please check your input.');
        } else {
          throw new Error('Failed to create entity. Please try again later.');
        }
      }

      handleFormClear();
    } catch (error: any) {
      if (error.message) {
        setEntityFormData({
          ...entityFormData,
          formError: error.message,
        });
      }
    }

    setLoading(false);
  };

  let mainPageContent: JSX.Element;

  mainPageContent = (
    <div className="AddNewEntityPage">
      <h2 className="AddNewEntityTitle">Create New Entity</h2>
      <div className="AddNewEntityForm">
        <div className="AddNewEntityFormRow">
          <span className="AddNewEntityLabel">Entity Name *</span>
        </div>
        <div className="AddNewEntityFormRow">
          <InputField
            value={entityFormData.entityName}
            label={'Entity Name'}
            onValueChange={handleEntityNameChange}
          />
        </div>
        {entityFormData.entityNameError !== '' && (
          <div className="AddNewEntityFormRow">
            <span className="AddNewEntityError">
              {entityFormData.entityNameError}
            </span>
          </div>
        )}

        <div className="AddNewEntityFormRow">
          <span className="AddNewEntityLabel">Entity Email *</span>
        </div>
        <div className="AddNewEntityFormRow">
          <InputField
            value={entityFormData.entityEmail}
            label={'Entity Email'}
            onValueChange={handleEntityEmailChange}
          />
        </div>
        {entityFormData.entityEmailError !== '' && (
          <div className="AddNewEntityFormRow">
            <span className="AddNewEntityError">
              {entityFormData.entityEmailError}
            </span>
          </div>
        )}

        {entityFormData.formError !== '' && (
          <div className="AddNewEntityFormRow">
            <span className="AddNewEntityError">
              {entityFormData.formError}
            </span>
          </div>
        )}

        <div className="AddNewEntityFormSubmitRow">
          <button className="AddNewEntityFormButton" onClick={handleFormClear}>
            Clear
          </button>
          <button
            className="AddNewEntityFormButton AddNewEntityFormButtonPrimary"
            onClick={handleEntityCreation}
          >
            Create Entity
          </button>
        </div>
      </div>

      {loading && (
        <PageBlocker
          isDismissable={false}
          onDismiss={() => {}}
          children={<LoadingSpinner />}
        />
      )}
    </div>
  );

  return (
    <SidePanelScaffold
      isLoggedIn={loginState.isLoggedIn}
      isAdmin={loginState.isAdmin}
      stickyTopBar={true}
      isExpanded={isExpanded}
      showCollapseIcons={true}
      onExpandedToggle={handleExpandToggle}
      mainPageChildren={
        <>
          {mainPageContent}
          {loginState.isLoggedIn && (
            <>
              <BugButton
                parentPageUrl={'/admin/entity/add'}
                token={token}
                nav={nav}
              />
              <FeedBackButton
                parentPageUrl={'/admin/entity/add'}
                token={token}
                nav={nav}
              />
            </>
          )}
        </>
      }
      sideBarChildren={<AdminSideBarContent nav={nav} />}
      topBarChildren={
        <>
          <MainLogo nav={nav} />
          <div />
        </>
      }
    />
  );
}

export default AddNewEntityPage;
